import React, { useMemo, useState } from 'react';
import {
  datetime,
  dayjsToMoment,
  type Moment,
} from '@common/services/datetime';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Typography,
} from 'antd';

import { FontSize } from '@common/tokens';
import { useMutation, useQuery } from '@common/hooks';
import { Divider, LoadingState, Icon } from '@common/components';
import {
  AddHoursFormOptions,
  ManagerHourBlockErrorsPreview,
  ManagerUpsertHours,
} from '@timekeeping/graphql';
import {
  calculateTotalSeconds,
  findById,
  maybeParseInTz,
  withMinutesPrecision,
} from '@timekeeping/services';
import { createMessage } from 'works/legacy/lib/message-helper';
import { DrawerBody, DrawerFooter } from '@pay-bill/components';
import {
  ReviewWorkerTimesheetFragment,
  ReviewHourBlockFragment,
  WorksTimekeepingAddHoursInput,
} from '@common/graphql/schema';

const { Text } = Typography;

type FormValues = {
  facilityId: string | undefined;
  unitId: string | undefined;
  hourTypeId: string | undefined;
  clockIn?: Moment;
  clockOut?: Moment;
  skippedLunchBreak: boolean | undefined;
  breakStart?: Moment;
  breakEnd?: Moment;
};

export const HourBlockForm = ({
  workerTimesheet,
  hourBlock,
  action,
  onSuccess,
}: {
  workerTimesheet?: ReviewWorkerTimesheetFragment | undefined;
  hourBlock?: ReviewHourBlockFragment | undefined;
  action: 'edit' | 'add' | 'restore';
  onSuccess?: () => void;
}) => {
  const [form] = Form.useForm();
  const [noBreak, setNoBreak] = useState(hourBlock?.skippedLunchBreak);
  const [touched, setTouched] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>(() => ({
    facilityId: hourBlock?.facility!.id,
    unitId: hourBlock?.unit!.id,
    hourTypeId: hourBlock?.hourType!.id,
    clockIn: maybeParseInTz(
      hourBlock?.inOut.startedAt,
      hourBlock?.inOut.startedAtTz
    ),
    clockOut: maybeParseInTz(
      hourBlock?.inOut.endedAt,
      hourBlock?.inOut.endedAtTz
    ),
    skippedLunchBreak: hourBlock?.skippedLunchBreak,
    breakStart: maybeParseInTz(
      hourBlock?.lunchBreak?.startedAt,
      hourBlock?.lunchBreak?.startedAtTz
    ),
    breakEnd: maybeParseInTz(
      hourBlock?.lunchBreak?.endedAt,
      hourBlock?.lunchBreak?.endedAtTz
    ),
  }));

  const effectiveWorkerTs = (hourBlock?.workerTimesheet || workerTimesheet)!;
  const optionsQuery = useQuery(AddHoursFormOptions, {
    fetchPolicy: 'cache-and-network',
    variables: {
      workerTimesheetId: effectiveWorkerTs.id,
      facilityId: formValues.facilityId,
    },
  });

  const formOptions = optionsQuery.data?.worksTimekeepingAddHoursOptions;

  const selectedFacility = formValues.facilityId
    ? findById(formOptions?.facilities || [], formValues.facilityId)!
    : undefined;

  const hourBlockInput: WorksTimekeepingAddHoursInput = {
    entries: [
      {
        id: hourBlock?.inOut.id,
        entryType: 'in_out' as const,
        startedAt: formValues.clockIn
          ?.tz(selectedFacility?.timeZone || 'UTC', true)
          ?.toISOString(),
        startedAtTz: selectedFacility?.timeZone,
        endedAt: formValues.clockOut
          ?.tz(selectedFacility?.timeZone || 'UTC', true)
          ?.toISOString(),
        endedAtTz: selectedFacility?.timeZone,
      },
      {
        id: hourBlock?.lunchBreak?.id,
        entryType: 'lunch_break' as const,
        startedAt: formValues.breakStart
          ?.tz(selectedFacility?.timeZone || 'UTC', true)
          ?.toISOString(),
        startedAtTz: selectedFacility?.timeZone,
        endedAt: formValues.breakEnd
          ?.tz(selectedFacility?.timeZone || 'UTC', true)
          ?.toISOString(),
        endedAtTz: selectedFacility?.timeZone,
      },
    ],
    skippedLunchBreak: noBreak,
    hourBlockId: hourBlock?.id,
    hourTypeId: formValues.hourTypeId!,
    facilityId: formValues.facilityId!,
    unitId: formValues.unitId!,
    workerTimesheetId: effectiveWorkerTs?.id,
    timesheetId: effectiveWorkerTs.timesheet.id,
  };

  const addFormFieldsFilledIn = (formValues: FormValues) => {
    return !!(
      formValues?.facilityId &&
      formValues?.unitId &&
      formValues?.hourTypeId &&
      formValues?.clockIn &&
      formValues?.clockOut &&
      (formValues?.skippedLunchBreak ||
        (formValues?.breakStart && formValues?.breakEnd))
    );
  };

  const skipErrorValidation = (action: string) => {
    if (action === 'edit') {
      return !touched;
    }

    if (action === 'add') {
      return !addFormFieldsFilledIn(formValues);
    }

    if (action === 'restore') {
      return false;
    }
  };

  const validations = useQuery(ManagerHourBlockErrorsPreview, {
    fetchPolicy: 'cache-and-network',
    skip: skipErrorValidation(action),
    variables: {
      input: hourBlockInput,
    },
  });

  const errorsPreview = validations.data?.preview;
  const errors = errorsPreview?.errors;

  const toggleBreak = (value: boolean) => {
    form.setFieldsValue({ breakStart: undefined, breakEnd: undefined });
    setFormValues({
      ...formValues,
      breakStart: undefined,
      breakEnd: undefined,
      skippedLunchBreak: value,
    });
    setNoBreak(value);
  };

  const [upsertHoursMutation, upsertMutationData] = useMutation(
    ManagerUpsertHours,
    {
      onCompleted: () => {
        onSuccess?.();
        createMessage(
          'success',
          action === 'restore' ? 'Hours restored.' : `Hours ${action}ed.`
        );
      },
      onError: () =>
        createMessage('error', `Failed to ${action} hours. Please try again.`),
    }
  );

  const onSubmit = () => {
    upsertHoursMutation({
      variables: {
        input: hourBlockInput,
      },
    });
  };

  const { minStart, maxEnd } = useMemo(() => {
    if (!formOptions) {
      return { maxStart: undefined, maxEnd: undefined };
    }

    // antd v4 DatePicker (`defaultPickerValue`) and `disabledDate` are Moment-
    // typed at runtime, so bridge the dayjs range bounds back to Moment. Field
    // VALUES stay Moment via the `timesheet` subdomain helpers (out of scope).
    // TODO(WORK-6545): switch these pickers to the `@common/components`
    // DatePicker wrapper (drops this bridge) as part of the `timekeeping/timesheet`
    // cutover. Not doable in the `breaks` scope: the wrapper's onChange hands a
    // dayjs to the Form, which crashes `withMinutesPrecision` (moment-only
    // `.seconds()`); it also needs `calculateTotalSeconds` migrated and the
    // line ~506 `*All times in {clockIn.format('z')}` label reworked to derive
    // from `selectedFacility.timeZone` (momentToDayjs drops the IANA zone, so
    // `.format('z')` regresses EST/PST → UTC).
    return {
      minStart: dayjsToMoment(datetime(formOptions.clockInRange.minimum)),
      maxEnd: dayjsToMoment(datetime(formOptions.clockInRange.maximum)),
    };
  }, [formOptions]);

  const disabledDate = (date: Moment) => {
    if (!minStart || !maxEnd) {
      return false;
    }

    return date.isBefore(minStart, 'date') || date.isAfter(maxEnd, 'date');
  };

  const formValid = (action: string) => {
    if (action === 'restore') {
      return errorsPreview?.preventsChanges === false;
    }

    return (
      touched &&
      !validations.loading &&
      errorsPreview?.preventsChanges === false
    );
  };

  if (optionsQuery.loading && !optionsQuery.data) {
    return <LoadingState />;
  }

  return (
    <>
      <DrawerBody>
        {action === 'restore' && (
          <span>Review details and save to restore these hours.</span>
        )}
        <Form
          className="hour-block-form"
          onFinish={onSubmit}
          layout="vertical"
          form={form}
          initialValues={formValues}
          onValuesChange={(changes) => {
            setTouched(true);
            setFormValues((values) => {
              const newState = { ...values, ...changes };

              newState.clockIn = withMinutesPrecision(newState.clockIn);
              newState.clockOut = withMinutesPrecision(newState.clockOut);
              newState.breakStart = withMinutesPrecision(newState.breakStart);
              newState.breakEnd = withMinutesPrecision(newState.breakEnd);

              return newState;
            });
          }}
        >
          <Row
            gutter={[0, 20]}
            align="middle"
            style={{ marginTop: action === 'restore' ? '0' : '-24px' }}
          >
            <Text strong className="hour-block-title">
              Location
            </Text>
          </Row>
          <Row gutter={[0, 16]} align="middle">
            <Col span={6}>
              <Text type="secondary">Facility</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="facilityId"
                validateStatus={errors?.facility?.[0] ? 'error' : ''}
                help={errors?.facility?.[0]?.message}
              >
                <Select
                  showSearch
                  className="facility-select"
                  size="large"
                  optionFilterProp="children"
                  placeholder="Search facilities"
                  disabled={optionsQuery.loading}
                  suffixIcon={<Icon icon="search" kind="light" size="sm" />}
                  onChange={() => {
                    form.setFieldsValue({ unitId: undefined });
                    setFormValues((value) => ({
                      ...value,
                      unitId: undefined,
                    }));
                  }}
                >
                  {formOptions?.facilities.map((facility, id) => (
                    <Select.Option
                      key={id}
                      value={facility.id}
                      title={facility.name}
                    >
                      {facility.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Text type="secondary">Unit / Cost Center</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="unitId"
                validateStatus={errors?.unit?.[0] ? 'error' : ''}
                help={errors?.unit?.[0]?.message}
              >
                <Select
                  showSearch
                  className="unit-cost-center-select"
                  size="large"
                  optionFilterProp="children"
                  placeholder="Search units / cost centers"
                  disabled={
                    formValues.facilityId === null || optionsQuery.loading
                  }
                  suffixIcon={<Icon icon="search" kind="light" size="sm" />}
                >
                  {formOptions?.units.map((unit, id) => (
                    <Select.Option key={id} value={unit.id} title={unit.name}>
                      {`${unit.name} (${unit.costCenter})`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Divider className="hour-block-drawer-divider" />
          <Row gutter={[0, 16]} align="middle">
            <Col span={24}>
              <Text strong className="hour-block-title">
                Hours
              </Text>
            </Col>
            <Col span={6}>
              <Text type="secondary">Hour Type</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="hourTypeId"
                validateStatus={errors?.hourType?.[0] ? 'error' : ''}
                help={errors?.hourType?.[0]?.message}
              >
                <Select
                  className="select-width hour-type-select"
                  size="large"
                  optionFilterProp="children"
                  placeholder="Select an hour type"
                  disabled={optionsQuery.loading}
                >
                  {formOptions?.hourTypes.map((hourType, id) => (
                    <Select.Option
                      key={id}
                      value={hourType.id}
                      title={hourType.name}
                    >
                      <span
                        style={{ height: 'auto', whiteSpace: 'break-spaces' }}
                      >
                        {hourType.name}
                      </span>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={6}>
              <Text type="secondary">Clock In</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="clockIn"
                validateStatus={errors?.inOut?.startedAt?.[0] ? 'error' : ''}
                help={errors?.inOut?.startedAt?.[0]?.message}
              >
                <DatePicker
                  className="select-width"
                  name="picker-clock-in"
                  size="large"
                  placeholder="Select a date & time"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  showToday={false}
                  defaultPickerValue={minStart}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={6}>
              <Text type="secondary">Clock Out</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="clockOut"
                validateStatus={errors?.inOut?.endedAt?.[0] ? 'error' : ''}
                help={errors?.inOut?.endedAt?.[0]?.message}
              >
                <DatePicker
                  className="select-width"
                  size="large"
                  name="picker-clock-out"
                  placeholder="Select a date & time"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  showToday={false}
                  defaultPickerValue={minStart}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={6}>
              <Text type="secondary">Lunch / Break Start</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="breakStart"
                validateStatus={
                  errors?.lunchBreak?.startedAt?.[0] ? 'error' : ''
                }
                help={errors?.lunchBreak?.startedAt?.[0]?.message}
              >
                <DatePicker
                  className="select-width"
                  size="large"
                  name="picker-break-start"
                  placeholder="Select a date & time"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  showToday={false}
                  disabled={noBreak || !formValues.clockIn}
                  defaultPickerValue={minStart}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="skippedLunchBreak">
                <Checkbox
                  onChange={(e) => toggleBreak(e.target.checked)}
                  checked={noBreak}
                >
                  No break
                </Checkbox>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Text type="secondary">Lunch / Break End</Text>
            </Col>
            <Col span={10} style={{ paddingRight: '16px' }}>
              <Form.Item
                name="breakEnd"
                validateStatus={errors?.lunchBreak?.endedAt?.[0] ? 'error' : ''}
                help={errors?.lunchBreak?.endedAt?.[0]?.message}
              >
                <DatePicker
                  className="select-width"
                  size="large"
                  name="picker-break-end"
                  placeholder="Select a date & time"
                  format="MM/DD/YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  showToday={false}
                  disabled={noBreak || !formValues.clockIn}
                  defaultPickerValue={minStart}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={8} />
            <Col span={6}>
              <Text type="secondary">Total Time</Text>
            </Col>
            <Col span={18} className="total-time-col">
              {calculateTotalSeconds(
                formValues.clockIn,
                formValues.clockOut,
                formValues.breakStart,
                formValues.breakEnd
              )}
            </Col>
            <Col span={24} className="time-zone-col">
              <Text>
                *All times in {formValues.clockIn?.format('z') || 'UTC'}
              </Text>
            </Col>
            {hourBlock?.notes && (
              <>
                <Col span={24}>
                  <Divider className="hour-block-drawer-divider" />
                </Col>
                <Col span={24}>
                  <Text strong className="hour-block-title">
                    Worker Note
                  </Text>
                </Col>
                <Col span={24}>
                  <Text className="hour-block-note">{hourBlock?.notes}</Text>
                </Col>
              </>
            )}
          </Row>
        </Form>
      </DrawerBody>
      <DrawerFooter>
        <Col span={9}>
          <Button
            className="submit-hour-block-button"
            type="primary"
            size="large"
            disabled={!formValid(action)}
            onClick={form.submit}
            loading={upsertMutationData.loading}
          >
            {action === 'edit' || action === 'restore' ? 'Save' : 'Add Hours'}
          </Button>
        </Col>
      </DrawerFooter>
      <style jsx>{`
        :global(.HourBlockDrawer .ant-form-item) {
          margin-bottom: 0;
        }
        :global(.HourBlockDrawer .ant-drawer-body) {
          padding: 0;
        }
        :global(.HourBlockDrawer .drawer-body) {
          height: 100%;
        }
        :global(.HourBlockDrawer .drawer-footer) {
          position: absolute;
          bottom: 0;
          width: 100%;
          background-color: white;
        }
        :global(.HourBlockDrawer .hour-block-form) {
          margin-bottom: 80px;
        }
        :global(.ant-select-dropdown .ant-select-item-option-content) {
          white-space: break-spaces;
        }
        :global(.select-width) {
          width: 100%;
        }
        :global(.hour-block-title) {
          font-size: ${FontSize.md};
          margin-top: 24px;
          margin-bottom: 6px;
        }
        :global(.hour-block-note) {
          padding-top: 8px;
          padding-bottom: 16px;
        }
        :global(.hour-block-drawer-divider) {
          margin: 16px 0 !important;
        }
      `}</style>
    </>
  );
};
